import { useEffect, useRef, useState, useCallback } from 'react';

/* ── Module definitions on 3 orbital rings ───────────────────────── */

const ORBITS = [
  {
    radius: 0.26,
    speed: 0.0003,
    modules: [
      { label: 'Attendance', angle: 0 },
      { label: 'Assessments', angle: 2.09 },
      { label: 'Faculty', angle: 4.19 },
    ],
  },
  {
    radius: 0.42,
    speed: 0.00018,
    modules: [
      { label: 'Clinical', angle: 0.52 },
      { label: 'Feedback', angle: 1.83 },
      { label: 'Rotations', angle: 3.14 },
      { label: 'Electives', angle: 4.45 },
    ],
  },
  {
    radius: 0.58,
    speed: 0.00011,
    modules: [
      { label: 'Early Warning', angle: 1.05 },
      { label: 'Outreach', angle: 2.62 },
      { label: 'Wellness', angle: 4.71 },
    ],
  },
];

/**
 * OrbitalSystem — 10 bright module nodes orbiting a central hub.
 * Designed for dark backgrounds. Canvas-rendered at 60fps.
 */
export function OrbitalSystem({ className = '' }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const anglesRef = useRef(
    ORBITS.map((orbit) => orbit.modules.map((m) => m.angle))
  );
  const mouseRef = useRef({ x: 0.5, y: 0.5, inside: false });
  const hoveredRef = useRef(null);
  const fadeRef = useRef(0);
  const [tooltip, setTooltip] = useState(null);

  const getPositions = useCallback((cx, cy, scale) => {
    const positions = [];
    ORBITS.forEach((orbit, oi) => {
      const r = orbit.radius * scale;
      orbit.modules.forEach((mod, mi) => {
        const a = anglesRef.current[oi][mi];
        positions.push({
          x: cx + Math.cos(a) * r,
          y: cy + Math.sin(a) * r * 0.55,
          mod, oi, mi,
        });
      });
    });
    return positions;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let dpr = window.devicePixelRatio || 1;

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        inside: true,
      };
    };
    const onMouseLeave = () => {
      mouseRef.current.inside = false;
      hoveredRef.current = null;
      setTooltip(null);
    };

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);

    let lastTime = 0;

    const draw = (time) => {
      const dt = lastTime ? time - lastTime : 16;
      lastTime = time;

      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      const cx = w * 0.5;
      const cy = h * 0.48;
      const scale = Math.min(w, h);

      ctx.clearRect(0, 0, w, h);

      // Fade-in
      if (fadeRef.current < 1) fadeRef.current = Math.min(1, fadeRef.current + dt * 0.001);
      ctx.globalAlpha = fadeRef.current;

      const speedMul = mouseRef.current.inside ? 2.5 : 1;

      // Update angles
      ORBITS.forEach((orbit, oi) => {
        orbit.modules.forEach((_m, mi) => {
          anglesRef.current[oi][mi] += orbit.speed * dt * speedMul;
        });
      });

      const positions = getPositions(cx, cy, scale);

      // ── Orbital rings — bright white dashed ──
      ORBITS.forEach((orbit, oi) => {
        const r = orbit.radius * scale;
        ctx.beginPath();
        ctx.ellipse(cx, cy, r, r * 0.55, 0, 0, Math.PI * 2);
        ctx.strokeStyle = oi === 0 ? 'rgba(255,255,255,0.18)' : oi === 1 ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.08)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
      });

      // ── Connection lines — gradient white ──
      positions.forEach(({ x, y }) => {
        const grad = ctx.createLinearGradient(cx, cy, x, y);
        grad.addColorStop(0, 'rgba(255,255,255,0.18)');
        grad.addColorStop(1, 'rgba(255,255,255,0.03)');
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(x, y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 0.7;
        ctx.stroke();
      });

      // ── Center hub ──
      // Glow
      const hubGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 40);
      hubGlow.addColorStop(0, 'rgba(255,255,255,0.12)');
      hubGlow.addColorStop(0.5, 'rgba(255,255,255,0.04)');
      hubGlow.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, 40, 0, Math.PI * 2);
      ctx.fillStyle = hubGlow;
      ctx.fill();

      // Outer ring
      ctx.beginPath();
      ctx.arc(cx, cy, 22, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.35)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Inner ring
      ctx.beginPath();
      ctx.arc(cx, cy, 15, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Hub label
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '700 11px Outfit, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('CQ', cx, cy);

      // ── Hit test ──
      let newHovered = null;
      if (mouseRef.current.inside) {
        const mx = mouseRef.current.x * w;
        const my = mouseRef.current.y * h;
        positions.forEach((pos, idx) => {
          if (Math.hypot(pos.x - mx, pos.y - my) < 24) newHovered = idx;
        });
      }

      if (newHovered !== hoveredRef.current) {
        hoveredRef.current = newHovered;
        if (newHovered !== null) {
          const p = positions[newHovered];
          setTooltip({ label: p.mod.label, x: p.x, y: p.y - 30 });
        } else {
          setTooltip(null);
        }
      }

      // ── Module nodes — white/bright ──
      positions.forEach(({ x, y, mod }, idx) => {
        const hovered = hoveredRef.current === idx;
        const nr = hovered ? 19 : 15;

        // Hover glow
        if (hovered) {
          const g = ctx.createRadialGradient(x, y, 0, x, y, 38);
          g.addColorStop(0, 'rgba(255,255,255,0.2)');
          g.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.beginPath();
          ctx.arc(x, y, 38, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }

        // Node circle — white border, semi-fill
        ctx.beginPath();
        ctx.arc(x, y, nr, 0, Math.PI * 2);
        ctx.fillStyle = hovered ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)';
        ctx.fill();
        ctx.strokeStyle = hovered ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)';
        ctx.lineWidth = hovered ? 1.5 : 1;
        ctx.stroke();

        // Bright inner dot
        ctx.beginPath();
        ctx.arc(x, y, hovered ? 4 : 3, 0, Math.PI * 2);
        ctx.fillStyle = hovered ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.6)';
        ctx.fill();

        // Always-on label
        ctx.fillStyle = hovered ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.4)';
        ctx.font = hovered ? '600 9px Outfit, sans-serif' : '400 8px Outfit, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(mod.label, x, y + nr + 5);
      });

      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [getPositions]);

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-default"
        style={{ display: 'block' }}
      />
      {tooltip && (
        <div
          className="absolute pointer-events-none px-3 py-1.5 rounded-lg bg-white/20 border border-white/20 backdrop-blur-md text-xs font-semibold text-white whitespace-nowrap -translate-x-1/2 shadow-lg"
          style={{ left: tooltip.x, top: tooltip.y, fontFamily: 'Outfit' }}
        >
          {tooltip.label}
        </div>
      )}
    </div>
  );
}
